import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T } from '../ProjectOverview/parts/stitch';
import { useProjectData } from '../ProjectOverview/ProjectDataContext';
import PhaseRail from './PhaseRail';
import StagePanel from './StagePanel';
import ClientPassportCard from './ClientPassportCard';
import PhaseSettingsDialog from './PhaseSettingsDialog';
import OpstartWizard from './OpstartWizard';
import GenResultDialog from './GenResultDialog';
import TeamsKanaalRegel from './TeamsKanaalRegel';
import VraagHetProject from './VraagHetProject';
import { eerlijkeFasen } from './rustigeKop';

/**
 * De projectflow: fasenbalk + werkvlak per fase + klantpaspoort. Dit is de
 * startpagina van /projects/:id; de oude tabbladen blijven bereikbaar via
 * ?weergave=tabs (ontsnappingsluik) en elk paneel opent als drawer via
 * ?paneel=<key> (onOpenPaneel — geen enkel tabblad wordt onbereikbaar).
 */
function ProjectFlowPage({ onOpenPaneel, onOpenDialog }) {
  const { id, project, tasks } = useProjectData();
  const [flow, setFlow] = useState(null);
  const [flowError, setFlowError] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [paspoortCompact, setPaspoortCompact] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?opstart=1 (deeplink vanaf het PL-dashboard) opent de wizard direct; de
  // parameter gaat er meteen weer af, zodat sluiten of verversen hem niet heropent.
  useEffect(() => {
    if (searchParams.get('opstart') !== '1') return;
    setWizardOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('opstart');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadFlow = useCallback(async () => {
    try {
      setFlowError(null);
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setFlow(await r.json());
    } catch (e) { setFlowError(e.message); }
  }, [id]);

  useEffect(() => { if (id) loadFlow(); }, [id, loadFlow]);

  const phases = useMemo(() => flow?.phases || [], [flow]);
  // Eerlijke status: geen 'klaar' ná een fase die nog niet af is (zie rustigeKop).
  const zichtbaar = useMemo(() => eerlijkeFasen(phases).filter((p) => p.is_enabled), [phases]);

  // Standaard-selectie: de laatst bekeken fase van dít project (onthouden),
  // anders de eerste fase die 'bezig' is, anders de eerste die nog niet klaar
  // is, anders de laatste.
  useEffect(() => {
    if (!zichtbaar.length) return;
    if (activeKey && zichtbaar.some((p) => p.phase_key === activeKey)) return;
    let onthouden = null;
    try { onthouden = localStorage.getItem(`flowFase:${id}`); } catch { /* prima */ }
    if (onthouden && zichtbaar.some((p) => p.phase_key === onthouden)) {
      setActiveKey(onthouden);
      return;
    }
    const bezig = zichtbaar.find((p) => p.status === 'bezig');
    const open = zichtbaar.find((p) => p.status !== 'klaar');
    setActiveKey((bezig || open || zichtbaar[zichtbaar.length - 1]).phase_key);
  }, [zichtbaar, activeKey, id]);

  const kiesFase = (key) => {
    setActiveKey(key);
    try { localStorage.setItem(`flowFase:${id}`, key); } catch { /* prima */ }
  };

  // ?fase=<phase_key> (deeplink, bv. Nog in te plannen → postproductie) kiest die fase,
  // mits het project hem heeft; de parameter gaat er daarna af, zodat hij een latere
  // keuze niet overschrijft. Staat ná de standaard-selectie, dus wint van "onthouden".
  const urlFase = searchParams.get('fase');
  useEffect(() => {
    if (!urlFase || !zichtbaar.length) return;
    if (zichtbaar.some((p) => p.phase_key === urlFase)) {
      setActiveKey(urlFase);
      try { localStorage.setItem(`flowFase:${id}`, urlFase); } catch { /* prima */ }
    }
    const next = new URLSearchParams(searchParams);
    next.delete('fase');
    setSearchParams(next, { replace: true });
  }, [urlFase, zichtbaar, id, searchParams, setSearchParams]);

  const schakelFaseUit = async (p) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/phases/${p.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: false }),
      });
      loadFlow();
    } catch { /* fase blijft staan */ }
  };

  const actieveFase = zichtbaar.find((p) => p.phase_key === activeKey) || null;
  const clientNames = project?.fields?.KlantnaamString || project?.fields?.Klantnaam || [];
  const clientName = Array.isArray(clientNames) ? clientNames[0] : clientNames || '';

  if (flowError) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.error, mb: 1 }}>
          Kon de projectflow niet laden: {flowError}
        </Typography>
        <Typography variant="body2" onClick={loadFlow}
          sx={{ fontFamily: T.fontBody, color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
          Opnieuw proberen
        </Typography>
      </Box>
    );
  }
  if (!flow) {
    return (
      <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  // Nog niet opgestart = nog geen enkele taak: dan staat de opstart-knop in
  // beeld. Daarna staat hij alleen nog in het ⋯-menu van de kop.
  const nogNietOpgestart = (tasks || []).length === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PhaseRail
            phases={phases}
            activeKey={activeKey}
            onSelect={kiesFase}
            onDisable={schakelFaseUit}
            onOpenSettings={() => setSettingsOpen(true)}
            driftCount={(flow.template_drift?.missing_phase_keys || []).length}
          />
        </Box>
        {nogNietOpgestart && (
          <Box component="button" type="button" onClick={() => setWizardOpen(true)} sx={{
            border: 0, bgcolor: 'transparent', p: 0, mt: 0.75, cursor: 'pointer', flexShrink: 0,
            fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.tertiary,
            '&:hover': { textDecoration: 'underline' },
          }}>
            Project opstarten →
          </Box>
        )}
      </Box>
      <TeamsKanaalRegel projectRecId={id} />
      <VraagHetProject projectRecId={id} />

      {/* Zonder klantprofiel is het paspoort één stille regel boven het werkvlak;
          mét profiel een kaart rechts. Zelfde element (CSS-volgorde), zodat het
          niet opnieuw laadt bij het omschakelen. */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mt: 2,
        flexDirection: paspoortCompact ? 'column' : { xs: 'column', lg: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <StagePanel phase={actieveFase} project={project} onOpenPaneel={onOpenPaneel} onOpenDialog={onOpenDialog} />
        </Box>
        <Box sx={paspoortCompact
          ? { order: -1, width: '100%' }
          : { width: { xs: '100%', lg: 300 }, flexShrink: 0,
            position: { lg: 'sticky' }, top: 12, alignSelf: { lg: 'flex-start' } }}>
          <ClientPassportCard project={project} clientName={clientName} flow={flow} onFlowChanged={loadFlow}
            onCompactChange={setPaspoortCompact} />
        </Box>
      </Box>

      <OpstartWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        project={project}
        onOpenDialog={onOpenDialog}
        onOpenFasen={() => setSettingsOpen(true)}
        onOpenPaneel={onOpenPaneel}
        onDebriefResultaat={setGenResult}
      />
      <GenResultDialog result={genResult} onClose={() => setGenResult(null)} projectRecId={id} />

      <PhaseSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        projectRecId={id}
        phases={phases}
        driftKeys={flow.template_drift?.missing_phase_keys || []}
        onChanged={loadFlow}
      />
    </Box>
  );
}

export default ProjectFlowPage;
